import * as React from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { SmsMessage } from '../../../api/bulkSms';

const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text || text === '—') return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

function statusColor(status: string | null | undefined): 'success' | 'error' | 'warning' | 'default' {
  if (!status) return 'default';
  const s = status.toUpperCase();
  if (s === 'SUCCESS' || s === 'SENT') return 'success';
  if (s === 'FAILED' || s === 'REJECTED' || s === 'EXPIRED' || s === 'ABSENTSUBSCRIBER') return 'error';
  if (s === 'PENDING' || s === 'PROCESSING' || s === 'SUBMITTED' || s === 'BUFFERED') return 'warning';
  return 'default';
}

function DetailRow({
  label,
  value,
  copyable = false,
  icon,
  accentColor,
  bgColor,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  icon?: React.ReactNode;
  accentColor?: string;
  bgColor?: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      {icon && (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: bgColor ?? 'action.hover',
            color: accentColor ?? 'text.secondary',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
            {value || '—'}
          </Typography>
          {copyable && value && value !== '—' && (
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                {copied ? (
                  <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 13 }} color="action" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

export interface SmsMessageDetailsDialogProps {
  open: boolean;
  message: SmsMessage | null;
  onClose: () => void;
}

export default function SmsMessageDetailsDialog({
  open,
  message,
  onClose,
}: SmsMessageDetailsDialogProps) {
  const theme = useTheme();
  if (!message) return null;

  const colors = {
    primary: { main: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.1) },
    info: { main: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.1) },
    warning: { main: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.1) },
  };

  const deliveryTime = message.deliveryTime ? new Date(message.deliveryTime).toLocaleString() : '—';
  const createdOn = message.createdOn ? new Date(message.createdOn).toLocaleString() : '—';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, maxHeight: '92vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ChatRoundedIcon color="action" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700} component="span">
            Message Details
          </Typography>
        </Stack>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={0}>
          {/* Hero banner */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: (t) =>
                t.palette.mode === 'dark'
                  ? `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.18)} 0%, ${alpha(t.palette.primary.main, 0.06)} 100%)`
                  : `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.1)} 0%, ${alpha(t.palette.primary.main, 0.03)} 100%)`,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: colors.primary.main,
                  flexShrink: 0,
                }}
              >
                <PhoneRoundedIcon />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" fontWeight={800} noWrap>
                    {message.contact}
                  </Typography>
                  <Chip
                    label={message.deliveryStatus ?? 'Unknown'}
                    size="small"
                    color={statusColor(message.deliveryStatus)}
                    sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  Batch #{message.batchId} · {message.batchStatus}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ px: 2.5, py: 2, overflow: 'auto' }}>
            <Stack spacing={2}>
              {/* Message content */}
              <Card
                variant="outlined"
                sx={{
                  borderLeft: `3px solid ${colors.primary.main}`,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      fontWeight: 700,
                      color: colors.primary.main,
                    }}
                  >
                    Message
                  </Typography>
                  <Stack spacing={1.75}>
                    <DetailRow
                      label="Body"
                      value={message.message}
                      copyable
                      icon={<ChatRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.primary.main}
                      bgColor={colors.primary.bg}
                    />
                    <Divider />
                    <DetailRow
                      label="Purpose"
                      value={message.batchPurpose}
                      icon={<CampaignRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.warning.main}
                      bgColor={colors.warning.bg}
                    />
                  </Stack>
                </CardContent>
              </Card>

              {/* Delivery info */}
              <Card
                variant="outlined"
                sx={{
                  borderLeft: `3px solid ${colors.info.main}`,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      fontWeight: 700,
                      color: colors.info.main,
                    }}
                  >
                    Delivery
                  </Typography>
                  <Stack spacing={1.75}>
                    <DetailRow
                      label="Contact"
                      value={message.contact}
                      copyable
                      icon={<PhoneRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.info.main}
                      bgColor={colors.info.bg}
                    />
                    <Divider />
                    <DetailRow
                      label="Network"
                      value={message.deliveryNetwork || '—'}
                      icon={<RouterRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.info.main}
                      bgColor={colors.info.bg}
                    />
                    <Divider />
                    <DetailRow
                      label="Delivery time"
                      value={deliveryTime}
                      icon={<ScheduleRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.info.main}
                      bgColor={colors.info.bg}
                    />
                    <Divider />
                    <DetailRow
                      label="Status description"
                      value={message.statusDescription || '—'}
                      icon={<ChatRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.info.main}
                      bgColor={colors.info.bg}
                    />
                  </Stack>
                </CardContent>
              </Card>

              {/* Technical details */}
              <Card
                variant="outlined"
                sx={{
                  borderLeft: `3px solid ${colors.warning.main}`,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      fontWeight: 700,
                      color: colors.warning.main,
                    }}
                  >
                    Technical
                  </Typography>
                  <Stack spacing={1.75}>
                    <DetailRow
                      label="Message ID"
                      value={message.messageId}
                      copyable
                      icon={<FingerprintRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.warning.main}
                      bgColor={colors.warning.bg}
                    />
                    <Divider />
                    <DetailRow
                      label="Status code"
                      value={String(message.statusCode)}
                      icon={<CodeRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.warning.main}
                      bgColor={colors.warning.bg}
                    />
                    <Divider />
                    <DetailRow
                      label="Batch type"
                      value={message.batchType}
                      icon={<LayersRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.warning.main}
                      bgColor={colors.warning.bg}
                    />
                    <Divider />
                    <DetailRow
                      label="Created on"
                      value={createdOn}
                      icon={<ScheduleRoundedIcon sx={{ fontSize: 18 }} />}
                      accentColor={colors.warning.main}
                      bgColor={colors.warning.bg}
                    />
                  </Stack>
                </CardContent>
              </Card>

              {!message.deliveryStatus && (
                <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                  No delivery status yet — this message may still be in flight or was rejected
                  before a delivery report was generated.
                </Alert>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
